import { monthString } from "@/app/utilities/constants";
import { filterActiveVariants, formatPrice, formatPriceRange, getFirstModelImage } from "@/app/utilities/helper";
import type {
    GalleryCategory,
    GalleryCategoryId,
    GalleryColour,
    GalleryColourVariant,
    GalleryImageCategory,
    GallerySection,
    GallerySourceColour,
    GallerySourceImage,
    GallerySourceVariant,
    GallerySubcategory,
    ImageGalleryItem,
    ImagePageGalleryData,
    ImagePageModel,
    ModelImageGalleryData,
} from "./types";

const CDN_URL = process.env.NEXT_PUBLIC_CDN_URL || "";
const COLOUR_KEYS = ["colours", "colors", "available_colours", "available_colors", "color_options"] as const;
const IMAGE_SOURCE_KEYS = ["images", "gallery", "gallery_images"] as const;

const FALLBACK_CLASSIFY_RULES: {
    id: string;
    category: GalleryImageCategory;
    label: string;
    pattern: RegExp;
}[] = [
    { id: "steering", category: "interior", label: "Steering Wheel", pattern: /steering/i },
    { id: "dashboard", category: "interior", label: "Dashboard", pattern: /dashboard|infotainment|console|cluster/i },
    { id: "transmission", category: "interior", label: "Transmission", pattern: /gear|transmission|shifter/i },
    { id: "cabin", category: "interior", label: "Interior", pattern: /cabin|interior/i },
    { id: "seats", category: "interior", label: "Seats", pattern: /seat|upholster/i },
    { id: "boot", category: "interior", label: "Boot", pattern: /boot|trunk|luggage|cargo/i },
    { id: "wheels", category: "exterior", label: "Alloy Wheel", pattern: /wheel|alloy|tyre|tire/i },
    { id: "brakes", category: "exterior", label: "Brakes", pattern: /brake|caliper|disc/i },
    { id: "engine", category: "exterior", label: "Engine", pattern: /engine|bonnet|hood/i },
    { id: "suspension", category: "exterior", label: "Suspension", pattern: /suspension/i },
    { id: "grille", category: "exterior", label: "Grille", pattern: /grille|grill|headlamp|taillamp|headlight|roofline|bumper/i },
    { id: "overview", category: "exterior", label: "Front View", pattern: /front\s*(three|3\/4|quarter)/i },
    { id: "overview", category: "exterior", label: "Front View", pattern: /front/i },
    { id: "overview", category: "exterior", label: "Rear View", pattern: /rear/i },
    { id: "overview", category: "exterior", label: "Side Profile", pattern: /side|exterior|profile/i },
];

const GALLERY_CATEGORY_ICONS: Record<string, GalleryCategory["icon"]> = {
    all: "all",
    exterior: "exterior",
    interior: "interior",
    colour: "colour",
};

export const resolveModelImageUrl = (image: unknown): string => {
    const raw = typeof image === "string"
        ? image
        : (() => {
            if (!image || typeof image !== "object") return "";
            const record = image as Record<string, unknown>;
            for (const key of ["url", "src", "image", "path", "image_url"]) {
                const value = record[key];
                if (value) return String(value);
            }
            return "";
        })();

    if (!raw) return `${CDN_URL}placeholder-car.webp`;
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${CDN_URL}${raw.replace(/^\/+/, "")}`;
};

export const HERO_LCP_ASPECT = { width: 960, height: 600 } as const;
export const HERO_LCP_SIZES = "(min-width: 1024px) 66vw, 100vw";
export const HERO_LCP_SRCSET_WIDTHS = [640, 828, 960] as const;

export const getImagePageCdnUrl = (src: string, width: number, quality = 75) => {
    const trimmed = src.trim();
    if (!trimmed) return "";

    try {
        const url = /^https?:\/\//i.test(trimmed)
            ? new URL(trimmed)
            : new URL(`${CDN_URL}${trimmed.replace(/^\/+/, "")}`);
        url.searchParams.set("width", String(width));
        url.searchParams.set("format", "webp");
        url.searchParams.set("quality", String(quality));
        url.searchParams.delete("height");
        return url.toString();
    } catch {
        return trimmed;
    }
};

export const getImagePageLcpSrcSet = (src: string) =>
    HERO_LCP_SRCSET_WIDTHS.map((width) => `${getImagePageCdnUrl(src, width)} ${width}w`).join(", ");

export const getImagePageLcpUrl = (src: string, isMobile: boolean) =>
    getImagePageCdnUrl(src, isMobile ? 828 : 960);

const firstArray = <T>(modelObj: ImagePageModel, keys: readonly string[]): T[] => {
    const record = modelObj as unknown as Record<string, unknown>;
    for (const key of keys) {
        const value = record[key];
        if (Array.isArray(value) && value.length) return value as T[];
    }
    return [];
};

const pick = (obj: unknown, keys: string[]): string => {
    if (!obj || typeof obj !== "object") return "";
    const record = obj as Record<string, unknown>;
    for (const key of keys) {
        const value = record[key];
        if (value) return String(value);
    }
    return "";
};

const collectSourceImages = (modelObj: ImagePageModel): GallerySourceImage[] => {
    const images = firstArray<GallerySourceImage>(modelObj, IMAGE_SOURCE_KEYS);
    if (images.length) return images.filter(Boolean);
    const fallback = modelObj.featured_image || modelObj.image;
    return fallback ? [fallback] : [];
};

const searchText = (image: GallerySourceImage): string => {
    if (typeof image === "string") return image;
    return [
        image.label,
        image.title,
        image.alt,
        image.name,
        image.view,
        image.caption,
        image.category,
        image.type,
        image.subcategory,
        image.url,
        image.src,
    ]
        .filter(Boolean)
        .join(" ");
};

const classifyFallbackImage = (image: GallerySourceImage, index: number) => {
    const text = searchText(image);
    const explicitSub = typeof image === "string"
        ? ""
        : String(image.subcategory || image.sub_category || "").toLowerCase();
    const rule = FALLBACK_CLASSIFY_RULES.find(
        (item) => (explicitSub && item.id === explicitSub) || item.pattern.test(text),
    );
    const label = pick(image, ["label", "title", "alt", "name", "view", "caption"]);

    if (rule) {
        return {
            category: rule.category,
            subcategory: rule.id,
            label: label || rule.label,
        };
    }

    return {
        category: "exterior" as GalleryImageCategory,
        subcategory: "overview",
        label: label || `Image ${index + 1}`,
    };
};

const makeImageTitle = (carName: string, label: string, index: number) => {
    const view = String(label || "").replace(/^image\s*\d*$/i, "").trim();
    return view ? `${carName} ${view} Image` : `${carName} Image`;
};

const stripImageCountLabel = (value: string, carName: string) => {
    let view = value.trim();
    if (!view) return "";

    const prefix = carName.trim();
    if (prefix && view.toLowerCase().startsWith(prefix.toLowerCase())) {
        view = view.slice(prefix.length).trim();
    }

    view = view.replace(/\s*(?:[-–—]\s*)?(?:image\s*)?\d+\s*$/i, "").trim();
    view = view.replace(/\s+image$/i, "").trim();
    return /^image$/i.test(view) ? "" : view;
};

type CarImageNameSource = {
    label?: string;
    alt?: string;
    title?: string;
    src?: string;
};

/** Alt/title for a gallery frame: "{car} {view} Image", never "image 1". */
export const getCarImageName = (
    carName: string,
    image?: CarImageNameSource,
) => {
    const view =
        stripImageCountLabel(image?.label || "", carName) ||
        stripImageCountLabel(image?.title || "", carName) ||
        stripImageCountLabel(image?.alt || "", carName);

    return makeImageTitle(carName, view, 0);
};

export const getCarImageViewLabel = (
    carName: string,
    image?: CarImageNameSource,
) =>
    stripImageCountLabel(image?.label || "", carName) ||
    stripImageCountLabel(image?.title || "", carName) ||
    stripImageCountLabel(image?.alt || "", carName);

const findVariantSpec = (variant: ImagePageModel["base_variant"], key: string): string => {
    const current = Array.isArray(variant) ? variant[0] : variant;
    if (!current?.specSections) return "";

    const targetKey = key.toLowerCase().trim();
    for (const section of current.specSections) {
        for (const spec of section.specs ?? []) {
            if (spec.key?.toLowerCase().trim() === targetKey) {
                return String(spec.value ?? "").trim();
            }
        }
    }
    return "";
};

const toHex = (value: unknown) => {
    const raw = String(value || "").trim();
    if (/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) return raw;
    if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw}`;
    return "";
};

const buildModelVariants = (
    source: GallerySourceVariant[] | null | undefined,
    modelStatus: string | undefined,
    modelPageUrl: string,
): GalleryColourVariant[] =>
    filterActiveVariants(source ?? [], modelStatus)
        .map((variant: GallerySourceVariant, index: number): GalleryColourVariant | null => {
            const name = String(variant?.name ?? variant?.variant_name ?? "").trim();
            if (!name) return null;
            const slug = String(variant?.slug ?? variant?.variant_slug ?? "").trim();
            return {
                id: String(variant?._id ?? variant?.id ?? `variant-${index}`),
                name,
                price: variant?.price ? String(formatPrice(Number(variant.price), { includeUnit: true })) : "",
                href: modelPageUrl && slug ? `${modelPageUrl}/${slug}` : undefined,
            };
        })
        .filter((variant: GalleryColourVariant | null): variant is GalleryColourVariant => Boolean(variant));

const buildColours = (
    modelObj: ImagePageModel,
    fallbackImage: string,
    carName: string,
    sourceColours?: GallerySourceColour[] | null,
    modelPageUrl = "",
    modelVariants: GalleryColourVariant[] = [],
): GalleryColour[] => {
    const colours = sourceColours?.length ? sourceColours : firstArray<GallerySourceColour>(modelObj, COLOUR_KEYS);
    return colours.map((colour, index) => {
        const name = colour?.name || colour?.title || colour?.color_name || `Colour ${index + 1}`;
        const title = `${carName} ${name} Colour Image`;
        const linkedIds = (colour?.variant_ids ?? [])
            .map((variant) => String(variant?._id || variant?.id || "").trim())
            .filter(Boolean);
        const namedOnColour = (colour?.variant_ids ?? [])
            .map((variant) => {
                const variantName = String(variant?.name || "").trim();
                if (!variantName) return null;
                const slug = String(variant?.slug || "").trim();
                return {
                    id: String(variant?._id || variant?.id || slug || variantName),
                    name: variantName,
                    price: variant?.price ? String(formatPrice(Number(variant.price), { includeUnit: true })) : "",
                    href: modelPageUrl && slug ? `${modelPageUrl}/${slug}` : undefined,
                };
            })
            .filter((variant): variant is NonNullable<typeof variant> => Boolean(variant));
        // Same rule as the Colours page popup: no linked ids means every variant,
        // otherwise only the variants that colour lists.
        let variants: GalleryColourVariant[] = namedOnColour;
        if (modelVariants.length) {
            if (!linkedIds.length) {
                variants = modelVariants;
            } else {
                const allowed = new Set(linkedIds);
                variants = modelVariants.filter((variant) => allowed.has(variant.id));
            }
        }
        return {
            id: String(colour?._id || colour?.id || colour?.slug || `colour-${index + 1}`),
            name,
            hex: toHex(colour?.hex) || toHex(colour?.hex_code) || toHex(colour?.color_code) || "#9CA3AF",
            image: resolveModelImageUrl(colour?.colorImage || colour?.color_image || colour?.image || colour?.url || fallbackImage),
            alt: title,
            title,
            variants,
        };
    });
};

const isGalleryImageCategory = (value: string): value is GalleryImageCategory =>
    value === "exterior" || value === "interior";

const buildFromGalleryApi = (
    galleryApi: ModelImageGalleryData,
    carName: string,
): Pick<ImagePageGalleryData, "images" | "sections" | "categories" | "subcategories" | "imageCount" | "summaryImage"> => {
    const images: ImageGalleryItem[] = [];
    const sections: GallerySection[] = [];
    const subcategories: GallerySubcategory[] = [{ id: "all", label: "All", category: "all" }];
    const categories: GalleryCategory[] = [{ id: "all", label: "All", icon: "all" }];
    let summaryImage = "";

    for (const section of galleryApi.sections ?? []) {
        if (!isGalleryImageCategory(section.key)) continue;

        categories.push({
            id: section.key,
            label: section.label,
            icon: GALLERY_CATEGORY_ICONS[section.key] ?? "all",
        });

        for (const category of section.categories ?? []) {
            const categoryKey = String(category.key || category.label || "").trim() || `item-${subcategories.length}`;
            let subcategoryId = categoryKey.startsWith(`${section.key}-`)
                ? categoryKey
                : `${section.key}-${categoryKey}`;
            if (subcategories.some((item) => item.id === subcategoryId)) {
                subcategoryId = `${subcategoryId}-${subcategories.length}`;
            }

            subcategories.push({
                id: subcategoryId,
                label: category.label,
                category: section.key,
            });

            const categoryImages: ImageGalleryItem[] = [];

            for (const imageType of category.image_types ?? []) {
                for (const image of imageType.images ?? []) {
                    const src = resolveModelImageUrl(image.image_url);
                    const label = imageType.label || category.label;
                    const alt = image.alt || image.title || `${carName} ${label}`;
                    const title = image.title || image.alt || alt;

                    const item: ImageGalleryItem = {
                        id: images.length + 1,
                        src,
                        label,
                        alt,
                        title,
                        category: section.key,
                        subcategory: subcategoryId,
                        sectionId: subcategoryId,
                    };

                    images.push(item);
                    categoryImages.push(item);

                    if (!summaryImage && image.is_primary) {
                        summaryImage = src;
                    }
                }
            }

            if (categoryImages.length > 0) {
                sections.push({
                    id: subcategoryId,
                    title: category.label,
                    description: `${categoryImages.length} official ${category.label.toLowerCase()} images.`,
                    category: section.key,
                    images: categoryImages,
                });
            }
        }
    }

    return {
        images,
        sections,
        categories,
        subcategories,
        imageCount: galleryApi.image_count || images.length,
        summaryImage: summaryImage || images[0]?.src || "",
    };
};

const buildFromModelImages = (
    modelObj: ImagePageModel,
    carName: string,
): Pick<ImagePageGalleryData, "images" | "sections" | "categories" | "subcategories" | "imageCount" | "summaryImage"> => {
    const images: ImageGalleryItem[] = collectSourceImages(modelObj).map((image, index) => {
        const classified = classifyFallbackImage(image, index);
        const title = makeImageTitle(carName, classified.label, index);
        const sectionId = `${classified.category}-${classified.subcategory}`;

        return {
            id: index + 1,
            src: resolveModelImageUrl(image),
            label: classified.label,
            alt: title,
            title,
            category: classified.category,
            subcategory: sectionId,
            sectionId,
        };
    });

    const sectionMap = new Map<string, GallerySection>();
    for (const image of images) {
        const existing = sectionMap.get(image.sectionId);
        if (existing) {
            existing.images.push(image);
            continue;
        }

        sectionMap.set(image.sectionId, {
            id: image.sectionId,
            title: image.label,
            description: `${image.label} images.`,
            category: image.category,
            images: [image],
        });
    }

    const sections = Array.from(sectionMap.values()).filter((section) => section.images.length > 0);

    // These images come straight from the model record, not the image gallery
    // API, so there is no real category data behind them. The Exterior/Interior
    // split above is only a guess from file names — keep it for page sections,
    // but never offer it as a filter. Only "All" is shown.
    const categories: GalleryCategory[] = [{ id: "all", label: "All", icon: "all" }];
    const subcategories: GallerySubcategory[] = [{ id: "all", label: "All", category: "all" }];

    return {
        images,
        sections,
        categories,
        subcategories,
        imageCount: images.length,
        summaryImage: resolveModelImageUrl(getFirstModelImage(modelObj) || images[0]?.src || ""),
    };
};

/** Image page and the Images secondary-nav tab exist only when the gallery has at least this many images. */
export const MIN_IMAGES_FOR_IMAGE_PAGE = 8;

export const hasMinimumImagePageGallery = (imageCount: number) =>
    imageCount >= MIN_IMAGES_FOR_IMAGE_PAGE;

export const buildImagePageGalleryData = (
    modelObj: ImagePageModel,
    options?: {
        cityName?: string;
        galleryApi?: ModelImageGalleryData | null;
        colours?: GallerySourceColour[] | null;
        modelPageUrl?: string;
        /** Raw variant list of the model; used for colours that don't list their own variants. */
        variants?: GallerySourceVariant[] | null;
    },
): ImagePageGalleryData => {
    const carName = [modelObj.brand_name, modelObj.name].filter(Boolean).join(" ") || "Car";
    const fallbackImage = resolveModelImageUrl(getFirstModelImage(modelObj));
    const modelPageUrl = options?.modelPageUrl
        || (modelObj.brand_slug && modelObj.slug ? `/${modelObj.brand_slug}/${modelObj.slug}` : "");
    const modelVariants = buildModelVariants(options?.variants, modelObj.status, modelPageUrl);
    const colours = buildColours(modelObj, fallbackImage, carName, options?.colours, modelPageUrl, modelVariants);
    const specColors = findVariantSpec(modelObj.base_variant, "colors") || findVariantSpec(modelObj.base_variant, "colours");

    const galleryFromApi = options?.galleryApi
        ? buildFromGalleryApi(options.galleryApi, carName)
        : null;
    const fromGalleryApi = Boolean(galleryFromApi && galleryFromApi.images.length > 0);
    const galleryFromModel = galleryFromApi ?? buildFromModelImages(modelObj, carName);

    const images = galleryFromModel.images;
    const sections = galleryFromModel.sections;
    const baseCategories = galleryFromModel.categories;
    const subcategories = galleryFromModel.subcategories;
    const imageCount = galleryFromModel.imageCount;
    const summaryImage = galleryFromApi?.summaryImage || galleryFromModel.summaryImage || fallbackImage;

    const categories = [
        ...baseCategories,
        ...(colours.length > 0 ? [{ id: "colour" as const, label: "Colors", icon: "colour" as const }] : []),
    ];

    return {
        carName,
        source: galleryFromApi ? "gallery-api" : "model",
        priceLabel: formatPriceRange(modelObj.price_range),
        location: `Ex-showroom, ${options?.cityName || "Delhi"}`,
        imageCount,
        colorCount: colours.length || (specColors ? specColors.split(/[,|/]/).filter(Boolean).length : 0),
        summaryImage,
        // offersButtonText: `View ${monthString} Offers`,
        offersButtonText: `View Diwali Offers`,
        priceButtonText: "Get On Road Price",
        images,
        categories,
        subcategories,
        sections,
        colours,
        fromGalleryApi,
        variantTotal: modelVariants.length,
    };
};

export const getGalleryCategoryCounts = (data: ImagePageGalleryData) => {
    const counts: Record<GalleryCategoryId, number> = {
        all: (data.imageCount || data.images.length) + data.colours.length,
        exterior: 0,
        interior: 0,
        colour: data.colours.length,
    };

    for (const image of data.images) {
        counts[image.category] += 1;
    }

    return counts;
};

export const filterGalleryImages = (
    images: ImageGalleryItem[],
    category: GalleryCategoryId,
    subcategory: string,
) => {
    if (category === "colour") return [];
    return images.filter((image) => {
        const categoryMatch = category === "all" || image.category === category;
        const subcategoryMatch = subcategory === "all" || image.subcategory === subcategory;
        return categoryMatch && subcategoryMatch;
    });
};

export const getSectionIdForSubcategory = (subcategoryId: string): string | null => {
    if (subcategoryId === "all") return null;
    return subcategoryId;
};

export const getGalleryFilterOffset = () => {
    const filterBar = document.querySelector<HTMLElement>("[data-gallery-filter-bar]");
    const filterStyles = filterBar ? window.getComputedStyle(filterBar) : null;
    const filterTop = filterStyles ? Number.parseFloat(filterStyles.top) || 0 : 112;
    const filterHeight = filterBar?.getBoundingClientRect().height ?? 0;
    return filterTop + filterHeight + 12;
};

export const getActiveGallerySectionId = (sectionIds: string[]) => {
    const marker = getGalleryFilterOffset();
    const start = document.getElementById("gallery-sections-start");
    if (start && start.getBoundingClientRect().top > marker + 40) return "all";

    let activeId = "all";
    for (const sectionId of sectionIds) {
        const target = document.getElementById(`gallery-section-${sectionId}`);
        if (!target) continue;
        if (target.getBoundingClientRect().top <= marker + 40) {
            activeId = sectionId;
            continue;
        }
        break;
    }

    return activeId;
};

export const scrollToGallerySection = (subcategoryId: string) => {
    const sectionId = getSectionIdForSubcategory(subcategoryId);
    const targetId = sectionId ? `gallery-section-${sectionId}` : "gallery-sections-start";
    const target = document.getElementById(targetId);

    if (!target) return;

    const top = target.getBoundingClientRect().top + window.scrollY - getGalleryFilterOffset();
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
};

export const scrollToGalleryFilterBar = () => {
    const categoryTabs = document.getElementById("gallery-category-tabs");
    if (!categoryTabs) return;

    const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? 65;
    const secondaryNavHeight = document.querySelector("[data-secondary-nav]")?.getBoundingClientRect().height ?? 0;
    const top = categoryTabs.getBoundingClientRect().top + window.scrollY - headerHeight - secondaryNavHeight - 8;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
};
